import React, { useEffect, useState, useRef } from 'react';
import ghostLogo from '../../assets/images/brand/ghost_logo.svg';
import InlineEditor from '../ProjectPanel/InlineEditor';
import ContextMenu from '../Overlays/ContextMenu';
import Modal from '../Overlays/Modal';
import ModalShareProject from '../Overlays/ModalShareProject';
import Nav from '../SidePanel/Nav';
import Tooltip from '../Overlays/Tooltip';
import { apiGetProjects, apiLeaveProject, apiAcceptProjectInvite } from '../../api/projects';
import { createProject, renameProject, deleteProject } from '../../helpers/projectHelpers';
import { getAvatarInitials, getAvatarColorHex } from '../../helpers/stringUtils';
import { useNavigate } from 'react-router-dom';
import { canRenameProject } from '../../helpers/projectAccess';
import { screenSafePlacement } from '../../helpers/uiHelpers';
import Icon from '../Icon';

export default function ProjectsPage({ user }) {
  const [projects, setProjects] = useState(null);
  const [loading, setLoading] = useState(false);
  const [menuOpenId, setMenuOpenId] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [pendingLeave, setPendingLeave] = useState(null);
  const [shareProject, setShareProject] = useState(null);
  const navigate = useNavigate();
  const menuRefs = useRef({});

  useEffect(() => {
    let mounted = true;
    async function load() {
      if (!user) return;
      setLoading(true);
      try {
        // Always fetch the authoritative project list from API so the UI stays fresh
        const data = await apiGetProjects(user.id, user.email);
        if (mounted) setProjects(data || []);
      } catch (err) {
        if (mounted) setProjects([]);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    return () => { mounted = false; };
  }, [user]);

  // Set page title on projects listing
  useEffect(() => {
    document.title = 'Projects | Ghost';
    return () => { document.title = 'Ghost'; };
  }, []);

  const refreshProjects = async () => {
    if (!user?.id) return;
    const data = await apiGetProjects(user.id, user.email);
    setProjects(data || []);
  };

  const handleOpen = async (project) => {
    if (!project?.id) return;

    if (project.is_shared && project.invite_status === 'invited' && user?.id) {
      await apiAcceptProjectInvite(project.id, user.id, user.email);
    }

    navigate(`/p/${project.id}`);
  };

  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');

  const handleCreate = async () => {
    if (!user?.id) return;
    try {
      const created = await createProject(user.id);
      if (!created?.id) {
        throw new Error('Project creation failed');
      }
      setProjects(prev => (prev ? [created, ...prev] : [created]));
      setEditingId(created.id);
      setEditName(created.name || 'New Project');
    } catch (err) {
      console.error('Create project error', err);
    }
  };

  const handleRename = async (projectId) => {
    const newName = (editName || '').trim() || 'Untitled Project';
    try {
      const updated = await renameProject(projectId, newName);
      setProjects(prev => prev.map(p => (p.id === projectId ? { ...p, name: updated.name } : p)));
    } catch (err) {
      console.error('Rename project error', err);
    } finally {
      setEditingId(null);
      setEditName('');
    }
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete || !user?.id) return;
    try {
      const success = await deleteProject(pendingDelete.id, user.id);
      if (success) {
        setProjects(prev => prev.filter(p => p.id !== pendingDelete.id));
        // Navigate to projects page if not already there (for future editor-based deletion)
        if (!window.location.pathname.includes('/projects')) {
          navigate('/projects');
        }
      }
    } catch (err) {
      console.error('Delete project error', err);
    } finally {
      setPendingDelete(null);
    }
  };

  const handleConfirmLeave = async () => {
    if (!pendingLeave || !user?.id) return;
    try {
      const success = await apiLeaveProject(pendingLeave.id, user.id);
      if (success) {
        setProjects(prev => prev.filter(p => p.id !== pendingLeave.id));
      }
    } catch (err) {
      console.error('Leave project error', err);
    } finally {
      setPendingLeave(null);
    }
  };

  const myProjects = (projects || []).filter(p => !p.is_shared);
  const sharedProjects = (projects || []).filter(p => p.is_shared);

  const renderProjectItem = (p) => {
    if (!menuRefs.current[p.id]) {
      menuRefs.current[p.id] = React.createRef();
    }

    const isOwned = !p.is_shared;
    const allowRename = canRenameProject(p);
    const contextItems = isOwned
      ? [
          ...(allowRename
            ? [
                {
                  label: 'Rename',
                  iconClass: 'pencil-simple',
                  onClick: () => {
                    setEditingId(p.id);
                    setEditName(p.name || 'Untitled Project');
                    setMenuOpenId(null);
                  }
                }
              ]
            : []),
          {
            label: 'Share',
            iconClass: 'users',
            onClick: () => {
              setShareProject(p);
              setMenuOpenId(null);
            }
          },
          {
            label: 'Delete',
            iconClass: 'trash',
            onClick: () => {
              setPendingDelete(p);
              setMenuOpenId(null);
            }
          }
        ]
      : [
          {
            label: 'Leave project',
            iconClass: 'user-minus',
            onClick: () => {
              setPendingLeave(p);
              setMenuOpenId(null);
            }
          }
        ];

    const safePlacement = screenSafePlacement('right', menuRefs.current[p.id]?.current, {
      itemCount: contextItems.length,
      minMenuWidth: 220
    });

    return (
      <div id={`project-${p.id}`} key={p.id} className="project-item">
        <div className="project-item-content">
          <div className="project-item-inner">
            {editingId === p.id && allowRename ? (
              <InlineEditor
                value={editName}
                onChange={setEditName}
                onSave={() => handleRename(p.id)}
                onCancel={() => { setEditingId(null); setEditName(''); }}
              />
            ) : (
              <h3 className="project-title" onClick={() => handleOpen(p)}>{p.name || p.title}</h3>
            )}
          </div>
          <div className="project-doc-count">
            {p.doc_count > 0 && (
              <span className="chip doc-count-badge">{p.doc_count} doc{p.doc_count !== 1 ? 's' : ''}</span>
            )}
          </div>

          <div className="project-collaborators">
            {p.has_public_link && (
              <div
                className="project-collab-avatar project-collab-avatar--public"
                data-tooltip="Anyone with the link"
                data-tooltip-placement="top"
                aria-label="Public share link active"
              >
                <Icon icon={'globe'} className="icon globe" />
              </div>
            )}
            {(p.collaborators || []).slice(0, p.has_public_link ? 3 : 4).map((collaborator, index, list) => (
              <div
                key={collaborator.id || collaborator.identifier || `${p.id}-${index}`}
                className={`project-collab-avatar${(index > 0 || p.has_public_link) ? ' stacked' : ''}`}
                style={{
                  backgroundColor: getAvatarColorHex(collaborator.identifier || collaborator.email),
                  zIndex: list.length - index
                }}
                data-tooltip={collaborator.email}
                data-tooltip-placement="top"
                aria-label={collaborator.email}
              >
                {getAvatarInitials(collaborator.email)}
              </div>
            ))}
          </div>
        </div>

        <div className="project-controls">
          <button
            ref={menuRefs.current[p.id]}
            type="button"
            className="iconButton"
            aria-label="Project options"
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpenId(menuOpenId === p.id ? null : p.id);
            }}
          >
            <Icon icon={'dots-three-vertical'} className="icon dots-three-vertical" />
          </button>
          {menuOpenId === p.id && (
            <ContextMenu
              triggerRef={menuRefs.current[p.id]}
              onClose={() => setMenuOpenId(null)}
              items={contextItems}
              placement={safePlacement}
            />
          )}
          <button
            type="button"
            className="iconButton"
            aria-label="Open project"
            onClick={(e) => {
              e.stopPropagation();
              handleOpen(p);
            }}
          >
            <Icon icon={'caret-right'} className="icon caret-right" />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div id="app">
      <Tooltip />
      <Nav 
        user={user} 
        hideButtons={['projectButton', 'outlineButton', 'chatsButton', 'versionsDivider', 'versionsButton']} 
        hideSettings={['Fit to Screen']}
      />
      <div id="main" className="projects-container">
        <div className="projects-box">
          <div className="logo">
            <img src={ghostLogo} alt="Ghost Logo" className="projects-logo" />
          </div>

        <div className="projects-list-wrapper">
          <div className="projects-list-header">
            <h2>My Projects</h2>
            <div className="projectControls">
              <button
                type="button"
                className="iconButton plusButton"
                onClick={handleCreate}
                aria-label="Create project"
              >
                <Icon icon={'plus'} className="icon plus" />
              </button>
            </div>
          </div>

          <div className="projects-list">
            {loading && <p className="placeholder">Loading projectsâ€¦</p>}
            {!loading && (!projects || projects.length === 0) && <p className="placeholder">No projects found.</p>}
            {!loading && myProjects.map(renderProjectItem)}

            {!loading && projects && projects.length > 0 && (
              <div className="projects-subheader">
                <h3>Shared with me</h3>
              </div>
            )}
            {!loading && sharedProjects.length === 0 && projects && projects.length > 0 && (
              <p className="placeholder">No shared projects yet.</p>
            )}
            {!loading && sharedProjects.map(renderProjectItem)}
          </div>
        </div>
      </div>
    </div>

      <Modal
        isOpen={!!pendingDelete}
        title="Confirm Delete Project"
        message={
          pendingDelete
            ? `Are you sure you want to delete "${pendingDelete.name}"?${pendingDelete.doc_count > 0 ? ` This will permanently delete all ${pendingDelete.doc_count} document${pendingDelete.doc_count !== 1 ? 's' : ''} and their content.` : ' This action cannot be undone.'}`
            : ''
        }
        confirmText="Delete Project"
        cancelText="Cancel"
        onConfirm={handleConfirmDelete}
        onCancel={() => setPendingDelete(null)}
      />

      <Modal
        isOpen={!!pendingLeave}
        title="Leave project"
        message={pendingLeave ? `Are you sure you want to leave "${pendingLeave.name}"?` : ''}
        confirmText="Leave project"
        cancelText="Cancel"
        onConfirm={handleConfirmLeave}
        onCancel={() => setPendingLeave(null)}
      />

      <ModalShareProject
        isOpen={!!shareProject}
        onClose={async () => {
          setShareProject(null);
          await refreshProjects();
        }}
        project={shareProject}
        user={user}
      />
    </div>
  );
}

